import { useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { usePageViewLog } from '@/hooks/usePageViewLog'
import { Loader2 } from 'lucide-react'
import { TopBar } from './TopBar'
import { PageTitleProvider } from './PageTitle'
import { AuditTrailDialogProvider } from '@/components/audit/AuditTrailDialog'
import { UndoToastProvider } from '@/components/inventory/storage/UndoToast'
import { IdleSessionGuard } from '@/components/session/IdleSessionGuard'
import { useNotificationsSocket } from '@/hooks/useNotificationsSocket'
import { fallbackFor } from '@/lib/tenantSwitchFallback'
import { useTenant } from '@/providers/TenantProvider'

export function Layout() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const isDashboard = pathname === '/dashboard'
  const { isTenantResolved, selectedTenantId } = useTenant()
  // Single mount point for the realtime notifications socket (covers every
  // authed route, including /dashboard where the TopBar is hidden).
  useNotificationsSocket()
  // Every page opened, for the User audit trail.
  usePageViewLog()

  // Leave a detail URL whose record belongs to the tenant we just left, before
  // the remount below fires its loaders against an id the new tenant cannot see.
  //
  // The ref is load-bearing: this must fire only on a real switch (one non-null
  // tenant to a different one), never on the first resolve. Without it, cold
  // deep-linking into a procedure would bounce to /patients the moment the tenant
  // list arrives.
  const previousTenantId = useRef(selectedTenantId)
  useEffect(() => {
    const previous = previousTenantId.current
    previousTenantId.current = selectedTenantId
    if (previous === null || selectedTenantId === null || previous === selectedTenantId) return
    const target = fallbackFor(pathname)
    if (target) navigate(target, { replace: true })
    // `pathname` is a dependency, so this also re-runs on plain navigation —
    // harmless, because the ref comparison above early-returns whenever the
    // tenant has not actually changed.
  }, [selectedTenantId, navigate, pathname])

  // Hold every authed route until the tenant selection is settled: login clears
  // cgt_selected_tenant (clearAuth), so a page rendering before /tenants
  // resolves would fire requests with no X-Tenant-Id header and 400/403 on
  // tenant-scoped endpoints. One spinner on cold load; warm loads read
  // localStorage synchronously and resolve on the first render.
  if (!isTenantResolved) {
    return (
      <div className='flex h-screen items-center justify-center bg-background'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    )
  }

  return (
    <div className='flex h-screen overflow-hidden bg-background'>
      {/*
        Outside the keyed Outlet below, so switching tenant does not remount the
        idle clock and hand the user a fresh window.
      */}
      <IdleSessionGuard />
      <PageTitleProvider>
      <div className='flex min-w-0 flex-1 flex-col'>
        {!isDashboard && <TopBar />}
        <main className={`flex-1 ${isDashboard ? 'overflow-hidden' : 'overflow-auto'}`}>
          {/*
            Keying on the tenant remounts the whole route subtree on a switch.
            TenantProvider clears the query cache, but that only reaches pages
            built on TanStack Query — a large share of pages here fetch with
            useEffect + useState and would otherwise keep showing the previous
            tenant's data until a manual reload (ProceduresPage fetches once per
            mount; PatientProcedurePage's loader keys on [procedureId, mrn]).
            Remounting re-runs every loader regardless of how it fetches.
            TopBar and the notifications socket sit outside this and survive.
          */}
          {/* The audit pop-up (z8nrz7dq6q) lives inside the keyed subtree so a
              tenant switch closes it along with the record it was opened from. */}
          <AuditTrailDialogProvider key={selectedTenantId ?? 'none'}>
            <UndoToastProvider>
              <Outlet />
            </UndoToastProvider>
          </AuditTrailDialogProvider>
        </main>
      </div>
      </PageTitleProvider>
    </div>
  )
}
